/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full border px-2 py-[3px] text-[11.5px] font-medium leading-none w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 [&>svg]:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] aria-invalid:ring-2 aria-invalid:ring-[var(--bad)]/30 aria-invalid:border-[var(--bad)] transition-colors overflow-hidden",
  {
    variants: {
      tone: {
        neutral: "bg-hover text-ink-2 border-line",
        ok: "bg-ok-bg text-ok border-ok-bd",
        warn: "bg-warn-bg text-warn border-warn-bd",
        bad: "bg-bad-bg text-bad border-bad-bd",
        info: "bg-brand-accent-soft text-brand-accent-ink border-brand-accent/30",
        solid: "bg-ink text-surface border-ink",
      },
      variant: {
        default: "bg-ink text-surface border-ink",
        secondary: "bg-hover text-ink-2 border-line",
        destructive: "bg-bad-bg text-bad border-bad-bd",
        outline: "bg-transparent text-ink-2 border-line",
        ghost: "bg-transparent text-ink-2 border-transparent",
        link: "bg-transparent text-brand-accent-ink border-transparent underline-offset-4 hover:underline",
      },
    },
    defaultVariants: {
      tone: "neutral",
    },
  }
)

type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>

const TONE_DOT_COLOR: Record<BadgeTone, string> = {
  neutral: "var(--ink-4)",
  ok: "var(--ok)",
  warn: "var(--warn)",
  bad: "var(--bad)",
  info: "var(--brand-accent)",
  solid: "var(--surface)",
}

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    dot?: boolean
  }

function Badge({
  className,
  variant,
  tone,
  asChild = false,
  dot,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot.Root : "span"
  const dotTone: BadgeTone = tone ?? "neutral"

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      data-tone={tone}
      className={cn(badgeVariants({ tone, variant }), className)}
      {...props}
    >
      {dot && (
        <span
          aria-hidden
          className="inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: TONE_DOT_COLOR[dotTone] }}
        />
      )}
      {children}
    </Comp>
  )
}

export { Badge, badgeVariants }
